/**
 * Reactive matchMedia hook. The viewer's mobile layout (handover §3.6)
 * needs a JS breakpoint where the mobile and desktop variants are
 * structurally different components (e.g. CarsTab's forced list
 * density), not just Tailwind-hidden duplicates of the same DOM.
 */

import { useEffect, useState } from 'react';

/**
 * The lg breakpoint, mirrored from Tailwind's default `lg:` (1024px).
 * Every JS viewport check MUST use this constant so the JS and CSS
 * halves of a responsive component can't drift apart.
 */
export const LG_MEDIA_QUERY = '(min-width: 1024px)';

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

export default useMediaQuery;
